import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { merge, Observable, takeUntil, tap } from 'rxjs';
import { ApplicationTypeEnum } from '../../../../core/constants/application-type-enum';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { ExpandableRadioButton } from '../../../../core/models/expandable-radio-button';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { ExpandableRadioButtonComponent } from '../../../../shared/components/fcc-601/expandable-radio-button/expandable-radio-button.component';
import { EllipsisPipe } from '../../../../shared/pipes/ellipsis.pipe';
import { atLeastOneCheckboxSelectedValidator } from '../../../../shared/validators/at-least-one-checkbox-selected.validator';
import { ApplicationPurposeCodeEnum } from '../../shared/constants/application-purpose-codes';
import { ApplicationValidationHelper } from '../../shared/helpers/application-validator/application-validator.helper';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { AttachmentRequiredButtonComponent } from '../shared/components/attachment/attachment-required-button/attachment-required-button.component';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import {
    AlienOwnershipExpandableRadioButtons,
    OperationPerformanceExpandableRadioButtons,
} from './constants/regulator-info-expandable-radio-buttons';
import { RegulatoryInfoMessages } from './constants/regulator-info-messages';
import { RegulatorInfoValidationKey } from './constants/regulator-info-validation-key';
import { RegulatoryInfoRequest } from './models/regulatory-info-request';
import { RegulatoryInfoResponse } from './models/regulatory-info-response';

@Component({
    selector: 'app-regulatory-info',
    templateUrl: './regulatory-info.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatRadioModule,
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatIconModule,
        MatExpansionModule,
        ApplicationFormFieldComponent,
        MatCheckboxModule,
        ExpandableRadioButtonComponent,
        EllipsisPipe,
        CommonModule,
        AttachmentRequiredButtonComponent,
    ],
})
export class RegulatoryInfoComponent
    extends ApplicationPageBaseComponent<
        RegulatoryInfoRequest,
        RegulatoryInfoResponse,
        RegulatoryInfoResponse
    >
    implements OnInit, OnDestroy
{
    private readonly directEntryService = inject(DirectEntryService);
    private readonly dropdownService = inject(DropdownService);

    yesNoOptions: DropdownOption[] = [];

    messages: typeof RegulatoryInfoMessages = RegulatoryInfoMessages;
    alienRulingOptions: typeof AlienOwnershipExpandableRadioButtons =
        AlienOwnershipExpandableRadioButtons;
    validationErrorKeys: typeof RegulatorInfoValidationKey =
        RegulatorInfoValidationKey;

    operationPerformanceOptions: typeof OperationPerformanceExpandableRadioButtons =
        OperationPerformanceExpandableRadioButtons;

    operationPerformanceCertificationOption: ExpandableRadioButton =
        OperationPerformanceExpandableRadioButtons.find(
            (option) => option.value === 'A',
        )!;

    radioServiceOfferingCheckboxes = this.getRadioServiceOfferingCheckboxes();
    radioServiceTypeCheckboxes = this.getRadioServiceTypeCheckboxes();

    override form: FormGroup<FormGroupType<RegulatoryInfoResponse>> =
        new FormGroup<FormGroupType<RegulatoryInfoResponse>>(
            {
                commonCarrier: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                nonCommonCarrier: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                privateCommunications: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                bandManager: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                broadcastServices: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                isFixed: new FormControl<boolean>(false, Validators.required),
                isMobile: new FormControl<boolean>(false, Validators.required),
                isRadioLocation: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                isSatellite: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                isBroadcastServicesTs: new FormControl<boolean>(
                    false,
                    Validators.required,
                ),
                isProvidingInterconnectedService: new FormControl<
                    boolean | null
                >(null, Validators.required),
                isForeignGovernment: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                isAlien: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                isAlienCorporation: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                isAlienOfficer: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                isAlienControl: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                alienRuling: new FormControl<string | null>(null),
                isRevoked: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                isConvictedFelon: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                isCourtAdjudicated: new FormControl<boolean | null>(
                    null,
                    Validators.required,
                ),
                operationPerformanceCertificationChoice: new FormControl<
                    string | null
                >(null),
                operationPerformanceCertificationAnswer: new FormControl<
                    boolean | null
                >({ value: null, disabled: true }),
                hasDiscontinuanceOfService: new FormControl<boolean | null>({
                    value: null,
                    disabled: true,
                }),
                hasRegulatoryCompliance: new FormControl<boolean | null>({
                    value: null,
                    disabled: true,
                }),
            },
            {
                validators: [
                    atLeastOneCheckboxSelectedValidator<RegulatoryInfoResponse>(
                        [
                            'commonCarrier',
                            'nonCommonCarrier',
                            'privateCommunications',
                            'bandManager',
                            'broadcastServices',
                        ],
                        this.validationErrorKeys
                            .atLeastOneRadioServiceOfferingRequired,
                    ),
                    atLeastOneCheckboxSelectedValidator<RegulatoryInfoResponse>(
                        [
                            'isFixed',
                            'isMobile',
                            'isRadioLocation',
                            'isSatellite',
                            'isBroadcastServicesTs',
                        ],
                        this.validationErrorKeys
                            .atLeastOneRadioServiceTypeRequired,
                    ),
                    this.conditionalAlienRulingValidator(),
                ],
            },
        );

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(Router),
            inject(ApplicationPageFacadeService),
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
        this.configureFormListeners();
        this.configureValidatorsBasedOnApplicationPurpose();
    }

    /**
     * Filters the operation performance options to exclude the certification option.
     * @returns the operation performance options that are not the certification option.
     */
    getFilteredOperationPerformanceOptions() {
        return this.operationPerformanceOptions.filter(
            (option) =>
                option.value !==
                this.operationPerformanceCertificationOption.value,
        );
    }

    /**
     * Determines whether the alien ruling radio buttons are invalid (required, but no selection).
     * @returns whether the validation has failed.
     */
    isAlienRulingInvalid(): boolean {
        const control = this.form.controls.alienRuling;

        return control
            ? (control.invalid && (control.touched || control.dirty)) ||
                  this.form.hasError(
                      this.validationErrorKeys.alienRulingRequired,
                  )
            : false;
    }

    isOperationPerformanceCertificationChoiceInvalid(): boolean {
        const control =
            this.form.controls.operationPerformanceCertificationChoice;

        return control
            ? control.invalid && (control.touched || control.dirty)
            : false;
    }

    /**
     * Determines whether any of the radio service offering checkboxes have been touched. This is used in combination with the
     * "at least one required" validator to determine when all of the checkboxes should be highlighted for the required error.
     */
    checkIfRadioServiceOfferingTouched = () =>
        this.checkIfFieldsTouched(this.radioServiceOfferingCheckboxes);

    /**
     * Determines whether any of the radio service type checkboxes have been touched. This is used in combination with the
     * "at least one required" validator to determine when all of the checkboxes should be highlighted for the required error.
     */
    checkIfRadioServiceTypeTouched = () =>
        this.checkIfFieldsTouched(this.radioServiceTypeCheckboxes);

    /**
     * Determines whether the current application purpose is either "Renewal Only" or "Renewal/Modification".
     */
    isApplicationPurposeRenewalsOrRenewalsWithModifications(): boolean {
        return (
            this.applicationState?.applicationPurpose ===
                ApplicationPurposeCodeEnum.renewalOnly ||
            this.applicationState?.applicationPurpose ===
                ApplicationPurposeCodeEnum.renewalOrModification
        );
    }

    /**
     * Determines whether the renewal certification fields (items 57-59) are enabled.
     * They are enabled for "Renewal Only" and "Renewal/Modification" applications,
     * except for Public Safety applications.
     */
    areRenewalCertificationsEnabled(): boolean {
        return (
            this.isApplicationPurposeRenewalsOrRenewalsWithModifications() &&
            !this.isPublicSafetyRenewal()
        );
    }

    /**
     * Determines whether the current application is a Public Safety "Renewal Only" or
     * "Renewal/Modification" application.
     */
    isPublicSafetyRenewal(): boolean {
        return (
            this.isApplicationPurposeRenewalsOrRenewalsWithModifications() &&
            this.applicationState?.applicationType ===
                ApplicationTypeEnum.PublicSafety
        );
    }

    private loadDropdownData() {
        this.dropdownService
            .getYesNoOptions()
            .pipe(
                takeUntil(this.destroy$),
                tap((options: DropdownOption[]) => {
                    this.yesNoOptions = options;
                }),
            )
            .subscribe();
    }

    /**
     * Checks whether one of two fields has a "Yes"/true selection. If one of them does, then the
     * alien ruling field is required.
     */
    private conditionalAlienRulingValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const form = control as FormGroup<
                FormGroupType<RegulatoryInfoResponse>
            >;
            const isAlienControl = form.controls.isAlienControl?.value;
            const isAlienOfficer = form.controls.isAlienOfficer?.value;
            const alienRuling = form.controls.alienRuling;

            if (
                (isAlienControl === true || isAlienOfficer === true) &&
                !alienRuling?.value
            ) {
                alienRuling?.setErrors({ required: true });
                return { [this.validationErrorKeys.alienRulingRequired]: true };
            } else {
                alienRuling?.setErrors(null);
            }

            return null;
        };
    }

    /**
     * Determines whether any fields within a group of fields have been touched.
     * @param fields the fields to check.
     * @returns whether at least one of the fields has been touched.
     */
    private checkIfFieldsTouched(
        fields: { name: keyof RegulatoryInfoResponse }[],
    ): boolean {
        return ApplicationValidationHelper.checkIfAnyFieldsIsTouched(
            this.form,
            fields.map((field) => field.name),
        );
    }

    private getRadioServiceOfferingCheckboxes(): {
        name: keyof RegulatoryInfoResponse;
        label: string;
    }[] {
        return [
            { name: 'commonCarrier', label: 'Common carrier' },
            { name: 'nonCommonCarrier', label: 'Non-common carrier' },
            {
                name: 'privateCommunications',
                label: 'Private, internal communications',
            },
            { name: 'broadcastServices', label: 'Broadcast services' },
            { name: 'bandManager', label: 'Band manager' },
        ];
    }

    private getRadioServiceTypeCheckboxes(): {
        name: keyof RegulatoryInfoResponse;
        label: string;
    }[] {
        return [
            { name: 'isFixed', label: 'Fixed' },
            { name: 'isMobile', label: 'Mobile' },
            { name: 'isRadioLocation', label: 'Radiolocation' },
            { name: 'isSatellite', label: 'Satellite (sound)' },
            { name: 'isBroadcastServicesTs', label: 'Broadcast services' },
        ];
    }

    /**
     * Configures the form validators based on the application's purpose and type.
     *
     * Note: Ensure that the application state is fully loaded before invoking this method,
     * as it relies on the application's purpose and type to determine the appropriate validation rules.
     */
    private configureValidatorsBasedOnApplicationPurpose(): void {
        if (this.areRenewalCertificationsEnabled()) {
            ApplicationValidationHelper.enableAndRequireFormControls(
                this.form,
                this.applicationPageFacadeService.applicationFormControlManager,
                [
                    'operationPerformanceCertificationAnswer',
                    'operationPerformanceCertificationChoice',
                    'hasDiscontinuanceOfService',
                    'hasRegulatoryCompliance',
                ],
            );
        }
    }

    /**
     * Configures the form listeners for the component.
     */
    private configureFormListeners(): void {
        this.handleOperationPerformanceCertificationChoiceChange();
        this.handleAlienOfficerAndControlChange();
    }

    /**
     * Handles the change of the operation performance certification choice field depending on the
     * operation performance certification answer field. We either want to select them both or deselect them both.
     */
    private handleOperationPerformanceCertificationChoiceChange(): void {
        const {
            operationPerformanceCertificationAnswer,
            operationPerformanceCertificationChoice,
        } = this.form.controls;

        operationPerformanceCertificationAnswer.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => {
                    operationPerformanceCertificationChoice.patchValue(
                        value !== null
                            ? this.operationPerformanceCertificationOption.value
                            : null,
                    );

                    if (
                        operationPerformanceCertificationAnswer.touched ||
                        operationPerformanceCertificationAnswer.dirty
                    ) {
                        operationPerformanceCertificationChoice.markAsTouched();
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Handles the enable/disable behavior and value clearing of the alien ruling (48b) field
     * based on responses to questions 47 (isAlienOfficer) and 48a (isAlienControl).
     * If either 47 or 48a is answered "Yes", the 48b radio group is enabled and becomes required.
     * If both are answered "No" or left blank, 48b is disabled and its value is cleared.
     */
    private updateAlienRulingState(): void {
        const { isAlienOfficer, isAlienControl, alienRuling } =
            this.form.controls;

        const officerYes = isAlienOfficer.value === true;
        const controlYes = isAlienControl.value === true;
        const shouldEnableAlienRuling = officerYes || controlYes;

        if (!shouldEnableAlienRuling) {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                alienRuling,
                { emitEvent: false },
                () => {
                    alienRuling.reset(null, { emitEvent: false });
                },
            );
        } else {
            this.applicationPageFacadeService.applicationFormControlManager.enable(
                alienRuling,
                { emitEvent: false },
                () => {},
            );
        }
    }

    /**
     * Handles reactive updates to the `alienRuling` field based on the values of
     * `isAlienOfficer` and `isAlienControl`. If neither is true, the field is disabled and cleared.
     * This runs on init and whenever either control value changes.
     */
    private handleAlienOfficerAndControlChange(): void {
        const { isAlienOfficer, isAlienControl } = this.form.controls;

        merge(isAlienOfficer.valueChanges, isAlienControl.valueChanges)
            .pipe(tap(() => this.updateAlienRulingState()))
            .subscribe();

        this.updateAlienRulingState();
    }

    protected override bindForm(response: RegulatoryInfoResponse): void {
        this.form.patchValue(response);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<RegulatoryInfoResponse> {
        return this.directEntryService.getRegulatoryInfo(applicationId);
    }

    protected override saveFormDataToApi(
        request: RegulatoryInfoRequest,
    ): Observable<StepValidationResult> {
        return this.directEntryService.saveRegulatoryInfo(request);
    }

    protected override generateRequestObject(): RegulatoryInfoRequest {
        const formValue = this.form.value as Required<RegulatoryInfoRequest>;

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }
}
